import { createContext, useState, useContext, useRef, useEffect, useCallback } from "react";

const SlideContext = createContext();

export const useSlideContext = () => useContext(SlideContext);

export const SlideContextProvider = ({ children }) => {
  const [activeSlideTitle, setActiveSlideTitle] = useState("");
  const [modifiedSlideTitle, setModifiedSlideTitle] = useState(false);
  const [activeSlideLayout, setActiveSlideLayout] = useState('');
  // const [hasPresentationAudio, setHasPresentationAudio] = useState(false);
  const [hasSlideAudio, setHasSlideAudio] = useState(false);
  const [selectModeStatus, setSelectModeStatus] = useState(false);
  const [blockOutlines, setBlockOutlines] = useState(false);
  const [showBlockTree, setShowBlockTree] = useState(false);
  const [activeSlideGrid, setActiveSlideGrid] = useState(false);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredBlock, setHoveredBlock] = useState(null);
  const [activeSelectedBlocks, setActiveSelectedBlocks] = useState(new Set());
  const [activeSelectedGroups, setActiveSelectedGroups] = useState(new Set());
  const [activeAvailableTools, setActiveAvailableTools] = useState([]);

  const currentEditorInstanceRef = useRef(null);
  const currentSlideRef = useRef(null);
  const toggleGridFunctionRef = useRef(null);
  const modifySlideTitleFunctionRef = useRef(null);
  const groupFunctionRef = useRef(null);
  const ungroupFunctionRef = useRef(null);
  const blockOutlinesFuncitonRef = useRef(null);
  const blockTreeUpdateFunctionRef = useRef(null);
  const blockOrderFunctionRef = useRef(null);
  const blockDuplicateFunctionRef = useRef(null);
  const selectClickHandlerRef = useRef(null);
  const startSelectModeFunctionRef = useRef(null);
  const endSelectModeFunctionRef = useRef(null);
  const deletBlocksFunctionRef = useRef(null);
  const addBlockFunctionRef = useRef(null);
  const hideBlockFunctionRef = useRef(null);
  const hideGroupFunctionRef = useRef(null);

  
  const handleKeyDown = useCallback((e) => {
    if(e.shiftKey){
      triggerStartSelectMode();
    }

    if (e.ctrlKey && !e.shiftKey && e.key.toLowerCase() === "g") {
      e.stopPropagation();
      e.preventDefault();
      triggerGroup();
    }

    if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "g") {
      e.stopPropagation();
      e.preventDefault();
      triggerUngroup();
    }

    if (e.ctrlKey && !e.shiftKey && e.key.toLowerCase() === "d") {
      e.stopPropagation();
      e.preventDefault();
      triggerBlockDuplicate();
    }

    if (e.ctrlKey && e.key === 'Delete'){
      e.stopPropagation();
      e.preventDefault();
      triggerDeleteBlocks();
    }

  }, []);

  const handleKeyUp = useCallback((e) => {
    if(!e.shiftKey){
      triggerEndSelectMode();
    }
  },[]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
    // eslint-disable-next-line
  }, [handleKeyDown]);

  // Registers
  const registerToggleGridFunction = (fn) => {
    toggleGridFunctionRef.current = fn;
  }

  const registerModifySlideTitle = (fn) => {
    modifySlideTitleFunctionRef.current = fn;
  }


  const registerStartSelectModeFunction = (fn) => {
    startSelectModeFunctionRef.current = fn;
  }

  const registerEndSelectModeFunction = (fn) => {
    endSelectModeFunctionRef.current = fn;
  }

  const registerGroupFunction = (fn) => {
    groupFunctionRef.current = fn;
  }

  const registerUngroupFunction = (fn) => {
    ungroupFunctionRef.current = fn;
  }

  const registerToggleBlockOutlinesFunction = (fn) => {
    blockOutlinesFuncitonRef.current = fn;
  }

  const registerCurrentEditorInstance = (editorInstance) => {
    currentEditorInstanceRef.current = editorInstance;
  }

  const registerCurrentSlideRef = (slideRef) => {
    currentSlideRef.current = slideRef;
  }

  const registerBlockTreeUpdateFunction = (fn) => {
    blockTreeUpdateFunctionRef.current = fn;
  }

  const registerBlockOrderFunction = (fn) => {
    blockOrderFunctionRef.current = fn;
  }
  
  const registerBlockDuplicateFunction = (fn) => {
    blockDuplicateFunctionRef.current = fn;
  }

  const registerSelectClickHandler = (fn) => {
    selectClickHandlerRef.current = fn;
  }

  const registerDeleteBlocksFunction = (fn) => {
    deletBlocksFunctionRef.current = fn;
  }

  const registerAddBlockFunction = (fn) => {
    addBlockFunctionRef.current = fn;
  }

  const registerHideBlockFunction = (fn) => {
    hideBlockFunctionRef.current = fn;
  }

  const registerHideGroupFunction = (fn) => {
    hideGroupFunctionRef.current = fn;
  }

// Triggers
  const triggerToggleGrid = () => {
    if (toggleGridFunctionRef.current) {
      toggleGridFunctionRef.current();
    }
  }

  const triggerModifySlideTitle = (value) => {
    if (modifySlideTitleFunctionRef.current) {
      modifySlideTitleFunctionRef.current(value);
    }
  }

  const triggerStartSelectMode = () => {
    if (startSelectModeFunctionRef.current) {
      startSelectModeFunctionRef.current();
    }
  }

  const triggerEndSelectMode = () => {
    if (endSelectModeFunctionRef.current) {
      endSelectModeFunctionRef.current();
    }
  }
  
  const triggerGroup = () => {
    if(groupFunctionRef.current){
      groupFunctionRef.current();
    }
  }

  const triggerUngroup = () => {
    if(ungroupFunctionRef.current){
      ungroupFunctionRef.current();
    }
  }

  const triggerBlockOutlines = () => {
    if(blockOutlinesFuncitonRef.current){
      blockOutlinesFuncitonRef.current();
    }
  }

  const triggerBlockTreeUpdate = () => {
    if(blockTreeUpdateFunctionRef.current){
      blockTreeUpdateFunctionRef.current();
    }
  }

  const triggerBlockOrder = (value) => {
    if(blockOrderFunctionRef.current){
      blockOrderFunctionRef.current(value);
    }
  }

  const triggerBlockDuplicate = () => {
    if(blockDuplicateFunctionRef.current){
      blockDuplicateFunctionRef.current();
    }
  }

  const triggerSelectClick = (blockId, groupId) => {
    if(selectClickHandlerRef.current){
      selectClickHandlerRef.current(blockId, groupId);
    }
  }

  const triggerDeleteBlocks = (blockIds) => {
    if(deletBlocksFunctionRef.current){
      deletBlocksFunctionRef.current(blockIds);
    }
  }

  const triggerAddBlock = (blockType) => {
    if(addBlockFunctionRef.current){
      addBlockFunctionRef.current(blockType);
    }
  }

  const triggerHideBlock = (blockId) => {
    if(hideBlockFunctionRef.current){
      hideBlockFunctionRef.current(blockId);
    }
  }

  const triggerHideGroup = (groupId) => {
    if(hideGroupFunctionRef.current){
      hideGroupFunctionRef.current(groupId);
    }
  }

  return (
    <SlideContext.Provider
      value={{
        activeSlideTitle, setActiveSlideTitle,
        modifiedSlideTitle, setModifiedSlideTitle,
        activeSlideLayout, setActiveSlideLayout,
        hasSlideAudio, setHasSlideAudio,
        selectModeStatus, setSelectModeStatus,
        blockOutlines, setBlockOutlines,
        showBlockTree, setShowBlockTree,
        activeSlideGrid, setActiveSlideGrid,
        hoveredNode, setHoveredNode,
        hoveredBlock, setHoveredBlock,
        activeSelectedBlocks, setActiveSelectedBlocks,
        activeSelectedGroups, setActiveSelectedGroups,
        activeAvailableTools, setActiveAvailableTools,
        registerToggleGridFunction,
        triggerToggleGrid,
        registerModifySlideTitle,
        triggerModifySlideTitle,
        registerGroupFunction,
        triggerGroup,
        registerUngroupFunction,
        triggerUngroup,
        registerToggleBlockOutlinesFunction,
        triggerBlockOutlines,
        registerCurrentEditorInstance,
        currentEditorInstanceRef,
        registerCurrentSlideRef,
        currentSlideRef,
        registerBlockTreeUpdateFunction,
        triggerBlockTreeUpdate,
        registerBlockOrderFunction,
        triggerBlockOrder,
        registerBlockDuplicateFunction,
        triggerBlockDuplicate,
        registerSelectClickHandler,
        triggerSelectClick,
        registerStartSelectModeFunction,
        triggerStartSelectMode,
        registerEndSelectModeFunction,
        triggerEndSelectMode,
        registerDeleteBlocksFunction,
        triggerDeleteBlocks,
        registerAddBlockFunction,
        triggerAddBlock,
        registerHideBlockFunction,
        triggerHideBlock,
        registerHideGroupFunction,
        triggerHideGroup,
      }}
    >
      {children}
    </SlideContext.Provider>
  );
}
